import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';

function brandLabel(brand) {
  if (!brand || brand === 'card') return null;
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

// 'ok' | 'soon' | 'expired' — from the card's expiry month/year.
function expiryStatus(expMonth, expYear) {
  if (!expMonth || !expYear) return 'ok';
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  // A card is valid THROUGH its exp month, so it dies at the start of the NEXT.
  const dies = new Date(expYear, expMonth, 1);
  if (dies <= thisMonth) return 'expired';
  const soon = new Date(now.getFullYear(), now.getMonth() + 2, 1);
  return dies <= soon ? 'soon' : 'ok';
}

function CardGlyph() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 40, height: 28, borderRadius: 6, flexShrink: 0,
        background: 'color-mix(in srgb, var(--accent, #b8862f) 14%, var(--bg-elev, transparent))',
        border: '1px solid color-mix(in srgb, var(--accent, #b8862f) 35%, transparent)',
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #b8862f)" strokeWidth="1.8">
        <rect x="2" y="5" width="20" height="14" rx="2.5" />
        <path d="M2 10h20" />
      </svg>
    </span>
  );
}


// Settings uses Stripe-hosted card entry. Onboarding keeps its own card gate.
export default function PaymentMethodCard({ disabled = false, managedPayments = false, onRefreshBilling }) {
  const { t } = useTranslation();
  const [state, setState] = useState(undefined);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [returnSession, setReturnSession] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? new URL(window.location.href).search : '');
    return params.get('card_update') === 'complete' ? params.get('card_update_session') : null;
  });
  const alive = useRef(true);
  const completionStarted = useRef(false);
  const refreshBilling = useRef(onRefreshBilling);
  refreshBilling.current = onRefreshBilling;

  async function loadCard() {
    try {
      const result = await api.billing.getPaymentMethod({ fresh: true });
      if (!alive.current) return;
      const data = result?.data ?? result ?? {};
      setState({ card: data.card ?? null, hasSubscription: data.hasSubscription === true,
        wallet: data.wallet ?? null, email: data.email ?? null });
      setLoadError(false);
    } catch (_) {
      if (alive.current) setLoadError(true);
    }
  }

  function clearReturnState() {
    const url = new URL(window.location.href);
    url.searchParams.delete('card_update');
    url.searchParams.delete('card_update_session');
    window.history?.replaceState(window.history.state, '', url.toString());
  }

  useEffect(() => {
    alive.current = true;
    loadCard();
    const refreshAfterBack = event => {
      if (!event.persisted) return;
      setStarting(false);
      loadCard();
      Promise.resolve(refreshBilling.current?.()).catch(() => {});
    };
    window.addEventListener('pageshow', refreshAfterBack);
    return () => { alive.current = false; window.removeEventListener('pageshow', refreshAfterBack); };
  }, []);

  async function finishUpdate() {
    if (!returnSession || disabled || starting) return;
    setStarting(true);
    setError(null);
    try {
      const response = await api.billing.updatePaymentMethod({ checkoutSessionId: returnSession });
      if (!alive.current) return;
      clearReturnState();
      setReturnSession(null);
      const result = response?.data ?? response;
      setSaved(!result?.superseded);
      if (result?.superseded) setError(t('paymentMethod.superseded'));
      await loadCard();
      Promise.resolve(refreshBilling.current?.()).catch(() => {});
    } catch (_) {
      if (alive.current) setError(t('paymentMethod.finishError'));
    } finally {
      if (alive.current) setStarting(false);
    }
  }

  useEffect(() => {
    if (!returnSession || disabled || completionStarted.current) return;
    completionStarted.current = true;
    finishUpdate();
  }, [returnSession, disabled]);

  async function openCardUpdate() {
    if (disabled || starting) return;
    setError(null);
    setSaved(false);
    setStarting(true);
    try {
      const returnUrl = new URL('/app/settings?tab=billing', window.location.href).toString();
      const response = await api.billing.createPortalSession({ returnUrl, flow: 'payment_method_update' });
      if (!alive.current) return;
      const result = response?.data ?? response;
      const url = result?.checkoutUrl || result?.portalUrl;
      if (!url) throw new Error('missing_card_update_url');
      window.location.assign(url);
    } catch (_) {
      if (alive.current) { setError(t('paymentIssue.portalError')); setStarting(false); }
    }
  }

  if (state === undefined && !loadError && !returnSession) {
    return <section className="s-card"><div className="s-card-head"><h3>{t('paymentMethod.title')}</h3></div>
      <div className="s-card-body"><div style={{ height: 28, width: 200, borderRadius: 8, background: 'var(--bg-elev)' }} /></div></section>;
  }
  const { card, hasSubscription, wallet, email } = state || {};
  if (!card && !hasSubscription && !loadError && !returnSession && !saved) return null;
  const isLink = !card && wallet === 'link';
  const exp = card ? expiryStatus(card.expMonth, card.expYear) : 'ok';
  const mm = card?.expMonth ? String(card.expMonth).padStart(2, '0') : '';
  const yy = card?.expYear ? String(card.expYear).slice(-2) : '';

  return <section className="s-card">
    <div className="s-card-head"><h3>{t('paymentMethod.title')}</h3></div>
    <div className="s-card-body">
      {state && <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <CardGlyph />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15 }}>
            <b>{card ? brandLabel(card.brand) || t('paymentMethod.card') : isLink ? t('paymentMethod.link') : t('paymentMethod.onFile')}</b>
            {card?.last4 && <span> •••• {card.last4}</span>}
            {isLink && email && <span> · {email}</span>}
          </div>
          {card && mm && yy && <div style={{ fontSize: 13, marginTop: 2, color: exp !== 'ok' ? 'var(--danger, #ef4444)' : 'var(--text-subtle)' }}>
            {t(exp === 'expired' ? 'paymentMethod.expired' : 'paymentMethod.expires', { mm, yy })}
            {exp === 'soon' ? ' · ' + t('paymentMethod.expiringSoon') : ''}
          </div>}
        </div>
        {!returnSession && <button type="button" className="btn btn-ghost btn-sm" onClick={openCardUpdate} disabled={starting || disabled}>
          {starting ? t('paymentIssue.openingPortal') : t('paymentMethod.updateOnStripe')}
        </button>}
      </div>}
      <p className="muted" style={{ fontSize: 13, margin: '12px 0 0' }}>{t('paymentMethod.hostedHint')}</p>
      {managedPayments && <p className="pm-managed-payments-note muted" style={{ fontSize: 13 }}>{t('paymentMethod.managedPaymentsNote')}</p>}
      {saved && <p role="status">✓ {t('paymentMethod.updated')}</p>}
      {loadError && <div role="alert" style={{ marginTop: 12 }}>
        {t('paymentMethod.loadError')} <button type="button" className="btn btn-ghost btn-sm" onClick={loadCard}>{t('paymentMethod.retry')}</button>
      </div>}
      {error && <p role="alert" style={{ color: 'var(--danger, #ef4444)' }}>{error}</p>}
      {returnSession && <button type="button" className="btn btn-violet btn-sm" onClick={finishUpdate} disabled={starting || disabled} style={{ marginTop: 12 }}>
        {starting ? t('paymentMethod.saving') : t('paymentMethod.retrySave')}
      </button>}
    </div>
  </section>;
}
